// Contratos de la API tal como RESPONDE hoy (medidos contra el backend real, docs/frontend/00 §2).
// Los decimales viajan como TEXTO: el frontend los muestra, nunca opera con ellos.
// Los enums se comparan con los de @madeci/ventas e @madeci/inventario en `contratos.test.ts`:
// si el backend agrega o renombra un estado, esa prueba falla antes que la interfaz mienta.

export type Decimal = string;
export type Uuid = string;

export const ESTADOS_NOTA = [
  'BORRADOR',
  'EN_APROBACION',
  'CONFIRMADA',
  'CERRADA_CORTA',
  'ANULADA',
  'DESCARTADA',
] as const;
export type EstadoNota = (typeof ESTADOS_NOTA)[number];

export const CANALES = ['VENDEDOR', 'CALL_CENTER', 'MESON', 'RUTA', 'B2B', 'POS'] as const;
export type Canal = (typeof CANALES)[number];

export const DOCUMENTOS_SOLICITADOS = ['FACTURA', 'BOLETA'] as const;
export type DocumentoSolicitado = (typeof DOCUMENTOS_SOLICITADOS)[number];
/** Lo que puede pedir una nota (0109): además de factura y boleta, el VALE de venta (no es DTE). */
export type DocumentoNota = DocumentoSolicitado | 'VALE';
/** Por qué se emite un vale y no una boleta o factura (0109). */
export const MOTIVOS_VALE = [
  ['VENTA', 'Venta'],
  ['COMPRA_PROPIA', 'Compra propia'],
  ['CONSUMO_INTERNO', 'Consumo interno'],
  ['CORTESIA', 'Cortesía'],
  ['OTRO', 'Otro'],
] as const;
export type MotivoVale = (typeof MOTIVOS_VALE)[number][0];
/** DESPACHO sale en reparto; RETIRO la retira el cliente en el local (nunca va a despacho). */
export const ENTREGAS_NOTA = ['DESPACHO', 'RETIRO'] as const;
export type EntregaNota = (typeof ENTREGAS_NOTA)[number];

export const CONDICIONES_PAGO = [
  'contado',
  'cheque_al_dia',
  'cheque_a_plazo',
  'credito',
  'transferencia',
] as const;
export type CondicionPago = (typeof CONDICIONES_PAGO)[number];

export const ORIGENES_PRECIO = ['LISTA', 'LISTA_ESCALA', 'MANUAL', 'COTIZACION'] as const;
export type OrigenPrecio = (typeof ORIGENES_PRECIO)[number];

export const TIPOS_CARGO = ['FLETE_VENTA', 'RECARGO'] as const;
export type TipoCargo = (typeof TIPOS_CARGO)[number];

export type ResultadoCredito = 'APROBADO' | 'REQUIERE_AUTORIZACION' | 'RECHAZADO';

/** Permiso que debe tener quien firma cada ámbito (espejo de PERMISO_DE_AMBITO, docs/93 §29). */
export const PERMISO_DE_AMBITO: Record<string, string> = {
  NOTA_VENTA: 'ventas.nota.aprobar',
  DESCUENTO: 'ventas.descuento.aprobar',
  PRECIO_MANUAL: 'ventas.precio.aprobar',
  CREDITO: 'ventas.credito.aprobar_excepcion',
  FLETE_VENTA: 'ventas.flete.aprobar',
  DEVOLUCION_CLIENTE: 'ventas.devolucion.aprobar',
  NOTA_CREDITO: 'ventas.nc.aprobar',
  NOTA_DEBITO: 'ventas.nd.aprobar',
  LISTA_PRECIO: 'ventas.nota.cambiar_lista_precio',
  SIN_PAGO: 'ventas.documento.sin_pago',
};

export const ESTADOS_DESPACHO = ['BORRADOR', 'CONFIRMADO', 'DESCARTADO'] as const;
export type EstadoDespacho = (typeof ESTADOS_DESPACHO)[number];
export const MODALIDADES_DESPACHO = ['DESPACHO', 'RETIRO', 'RUTA'] as const;
export type ModalidadDespacho = (typeof MODALIDADES_DESPACHO)[number];

export const TIPOS_UBICACION = [
  'BODEGA',
  'CAMION',
  'MESON',
  'MERMA',
  'CUARENTENA',
  'CONSIGNACION',
] as const;
export type TipoUbicacion = (typeof TIPOS_UBICACION)[number];
/** Desde qué tipos se vende y despacha (D-112): merma y cuarentena, no. */
export const UBICACIONES_DE_VENTA: readonly TipoUbicacion[] = [
  'BODEGA',
  'MESON',
  'CAMION',
  'CONSIGNACION',
];

// ---------------------------------------------------------------- sesión y organización

export interface SesionIniciada {
  usuarioId: Uuid;
  nombre: string;
  correo: string;
  expiraEn: string;
}

export interface Asignacion {
  id: Uuid;
  rol: string;
  empresa_id: Uuid | null;
}

export interface Yo {
  tipo: 'usuario' | string;
  id: Uuid;
  nombre: string;
  correo: string;
  estado: string;
  asignaciones: Asignacion[];
}

export interface Empresa {
  id: Uuid;
  codigo: string;
  rut: string;
  dv: string;
  razon_social: string;
  nombre_fantasia: string | null;
  activa: boolean;
  /** Datos de la ficha de la empresa (org.empresa) para los impresos. */
  giro?: string | null;
  direccion?: string | null;
  comuna?: string | null;
  correo_contacto?: string | null;
}

export interface PermisosEfectivos {
  empresaId: Uuid;
  permisos: string[];
}

export interface Ubicacion {
  id: Uuid;
  empresa_id: Uuid;
  codigo: string;
  nombre: string;
  tipo: TipoUbicacion | null;
}

// ---------------------------------------------------------------- clientes

export interface ClienteEncontrado {
  id: Uuid;
  rut: string | null;
  dv: string | null;
  razon_social: string;
  nombre_fantasia: string | null;
  giro: string | null;
  estado: 'activa' | 'bloqueada' | 'inactiva' | string;
  empresa_id: Uuid | null;
  condicion_pago: string | null;
  plazo_dias: number | null;
  bloqueado: boolean | null;
  /** Documento a emitir según la ficha del cliente (null = el del canal). */
  documento_por_defecto?: 'FACTURA' | 'BOLETA' | null;
  /** Rol con la empresa de la búsqueda. */
  cliente_empresa?: boolean;
  proveedor_empresa?: boolean;
}

export interface FichaEntidad {
  entidad: {
    id: Uuid;
    rut: string | null;
    dv: string | null;
    razon_social: string;
    nombre_fantasia: string | null;
    giro: string | null;
    estado: string;
    motivo_estado: string | null;
    tipo_persona: string;
  };
  empresas: Array<{
    empresa_id: Uuid;
    estado: string;
    condicion_pago: string;
    plazo_dias: number;
    linea_credito: Decimal | null;
    bloqueado: boolean;
    motivo_bloqueo: string | null;
    lista_precio_id: Uuid | null;
    /** Vendedor por defecto de la relación comercial (entidades.vendedor). */
    vendedor_id: Uuid | null;
    clasificacion: string | null;
    segmento: string | null;
    /** Observaciones comerciales de la relación (ronda 1). */
    observaciones?: string | null;
    /** Documento a emitir por defecto (ficha como Random); null = el del canal. */
    documento_por_defecto?: 'FACTURA' | 'BOLETA' | null;
    /** Sus facturas separan el costo logístico (línea LOGISTICA). */
    costo_logistico?: boolean;
    /** Días de visita/cobranza, lunes a domingo: X = sí, - = no. */
    dias_visita?: string | null;
    cobrador_id?: Uuid | null;
    /** Rol con esta empresa: cliente, proveedor o ambos. */
    es_cliente?: boolean;
    es_proveedor?: boolean;
    version?: number;
  }>;
  direcciones: Array<{
    id: Uuid;
    nombre: string | null;
    calle: string;
    numero: string | null;
    complemento: string | null;
    comuna_codigo: string | null;
    ciudad: string | null;
    es_principal: boolean;
    es_despacho: boolean;
    es_tributaria: boolean;
    activa: boolean;
    referencia?: string | null;
    version?: number;
    /** Procedencia de la marca de despacho: LEGACY·POR_VALIDAR = provisional (docs/frontend/16). */
    procedencia_origen?: 'LEGACY' | 'MADECI' | null;
    procedencia_estado?: 'POR_VALIDAR' | 'VALIDADA' | null;
  }>;
  contactos: Array<{
    id: Uuid;
    nombre: string;
    cargo: string | null;
    area: string;
    activo: boolean;
    version?: number;
  }>;
  medios: Array<{
    id: Uuid;
    contacto_id: Uuid | null;
    tipo: string;
    valor: string;
    proposito: string;
    principal: boolean;
    activo?: boolean;
    version?: number;
  }>;
}

export interface Vendedor {
  id: Uuid;
  empresa_id: Uuid;
  codigo: string;
  nombre: string;
  tipo: string;
  usuario_id: Uuid | null;
  activo: boolean;
}

/** 2A.1 · GET /ventas/contexto-comercial (docs/frontend/12 §11): lo decide el backend. */
export interface CondicionYPlazo {
  condicion: CondicionPago;
  plazoDias: number;
}
/** Catálogo del backend (decisión 2026-10-02): por qué la nota no lleva el vendedor del cliente. */
export type MotivoCambioVendedor =
  'DIGITACION_POR_VENDEDOR' | 'REASIGNACION_COMERCIAL' | 'VENTA_MESON' | 'VENTA_ESPECIAL' | 'OTRO';
/** OTRO exige observación (la valida el backend). */
export interface CambioVendedor {
  motivo: MotivoCambioVendedor;
  observacion?: string;
}
export interface ContextoComercial {
  empresaId: Uuid;
  clienteEntidadId: Uuid;
  canal: Canal;
  vendedor: {
    porDefecto: Uuid | null;
    permitidos: Array<{ id: Uuid; codigo: string; nombre: string }>;
    puedeCambiar: boolean;
    /** Distinto del vendedor del cliente exige uno de estos motivos. */
    motivosCambio: MotivoCambioVendedor[];
  };
  listaPrecio: {
    porDefecto: Uuid | null;
    permitidas: Array<{ id: Uuid; codigo: string; nombre: string; delCliente: boolean }>;
    puedeCambiar: boolean;
  };
  condicionPago: {
    porDefecto: CondicionYPlazo;
    permitidas: CondicionPago[];
    plazoMaximoDias: number;
    opciones: CondicionYPlazo[];
    puedeCambiar: boolean;
  };
  direcciones: Array<{
    id: Uuid;
    texto: string;
    comuna: string | null;
    comunaCodigo: string | null;
    esDespacho: boolean;
    esTributaria: boolean;
    zona: { id: Uuid; codigo: string; nombre: string } | null;
  }>;
  permisos: {
    cambiarVendedor: boolean;
    cambiarListaPrecio: boolean;
    cambiarCondicionPago: boolean;
  };
  cuentaCorriente:
    | { disponible: false; motivo: 'SIN_FUENTE_CXC_D118' }
    | { disponible: true; fuente: string; deuda: Decimal; vencidos: Decimal };
}

/** 2A.1 · GET /ventas/clientes/:id/ultimas-compras: una fila por línea comprada. */
export interface UltimaCompra {
  notaId: Uuid;
  numero: string;
  numeroVisible: string;
  /** La factura o boleta de esa compra (2026-10-26). */
  documentos?: Array<{ documentoId: string; tipoDte: number; folio: string | null }>;
  estado: EstadoNota;
  fecha: string;
  documento: DocumentoSolicitado;
  listaPrecioId: Uuid;
  linea: number;
  productoId: Uuid;
  sku: string;
  descripcion: string;
  presentacionId: Uuid;
  presentacionCodigo: string;
  cantidad: Decimal;
  cantidadBase: Decimal;
  precioUnitario: Decimal | null;
  descuento: Decimal;
  bruto: Decimal | null;
}

export interface ListaPrecio {
  id: Uuid;
  codigo: string;
  nombre: string;
  uso: string;
  moneda: string;
  semantica: string;
}

// ---------------------------------------------------------------- productos, precio, stock

export interface ProductoEncontrado {
  id: Uuid;
  sku: string;
  nombre: string;
  tipo: string;
  estado: string;
  unidad_base_codigo: string;
  empresa_id: Uuid | null;
  vendible: boolean | null;
  marca_id: Uuid | null;
  categoria_id: Uuid | null;
}

/** GET /productos/:id: la ficha del producto (lo que el puesto comercial usa de ella). */
export interface FichaProducto {
  producto: {
    id: Uuid;
    sku: string;
    nombre: string;
    unidad_base_codigo: string;
    /** Venta solo en múltiplos de esta cantidad en unidad base (envase cerrado); null = libre. */
    venta_multiplo: Decimal | null;
    version: number;
    nombre_corto?: string | null;
    tipo?: string;
    estado?: string;
    categoria_id?: Uuid | null;
    marca_id?: Uuid | null;
    peso_kg?: Decimal | null;
    volumen_m3?: Decimal | null;
    contenido_neto?: Decimal | null;
    contenido_unidad_codigo?: string | null;
    retornable?: boolean;
  };
  presentaciones: Presentacion[];
  codigos?: Array<Record<string, unknown>>;
  impuestos?: Array<Record<string, unknown>>;
}

// ---------------------------------------------------------------- ficha 360: cambios por campo

export type CampoFicha =
  | 'telefono'
  | 'email'
  | 'contacto'
  | 'direccion'
  | 'observaciones'
  | 'linea_credito'
  | 'condicion_pago'
  | 'lista_precio'
  | 'vendedor'
  | 'estado_comercial';

/** La política de un campo PARA el actor (la decide el backend). */
export interface PoliticaCampo {
  campo: CampoFicha;
  grupo: 'MAESTRO' | 'RELACION' | 'COMERCIAL';
  modo: 'EDICION_DIRECTA' | 'APROBACION_SUPERVISOR';
  descripcion: string;
  aplicacion: 'DIRECTA' | 'SOLICITUD' | 'NO_DISPONIBLE';
}

export interface ResultadoCambios {
  entidadId: Uuid;
  aplicados: CampoFicha[];
  pendientes: CampoFicha[];
  solicitudId: Uuid | null;
}

export type EstadoSolicitud = 'PENDIENTE' | 'APROBADA' | 'RECHAZADA' | 'CANCELADA';

export interface SolicitudCambio {
  id: Uuid;
  empresa_id: Uuid;
  entidad_id: Uuid;
  razon_social: string;
  rut: string | null;
  dv: string | null;
  estado: EstadoSolicitud;
  motivo: string;
  solicitado_por: Uuid;
  solicitado_en: string;
  decidido_por: Uuid | null;
  decidido_en: string | null;
  observacion_decision: string | null;
  version: number;
  campos: Array<{ campo: CampoFicha; valor_actual: unknown; valor_solicitado: unknown }>;
}

export interface Presentacion {
  id: Uuid;
  producto_id: Uuid;
  codigo: string;
  nombre: string;
  unidad_codigo: string;
  factor_a_base: Decimal;
  es_base: boolean;
  vendible: boolean;
  /** Se compra en esta presentación (el catálogo lo manda; las pantallas viejas no lo miran). */
  comprable?: boolean;
  activa: boolean;
}

export interface PrecioVigente {
  precioId: Uuid;
  listaId: Uuid;
  valor: Decimal;
  semantica: string;
  escala?: { desde: Decimal; hasta: Decimal | null; valor: Decimal };
}

export interface FilaDisponibilidad {
  empresaId: Uuid;
  ubicacionId: Uuid;
  productoId: Uuid;
  fisico: Decimal;
  reservado: Decimal;
  disponible: Decimal;
  sobrecomprometido: boolean;
  /** Clase de la diferencia con el legado SIN causa explicada; null si el saldo es confiable. */
  stockPorRevisar?: string | null;
}

// ---------------------------------------------------------------- nota de venta

export interface LineaEntrada {
  productoId: Uuid;
  presentacionId: Uuid;
  cantidad: Decimal;
  precioManual?: { precio: Decimal; motivo: string };
  descuento?: { tipo: 'PORCENTAJE' | 'MONTO'; valor: Decimal };
  recargo?: Decimal;
  /** Lista con que se precia esta línea; sin enviar, la de la nota. */
  listaPrecioId?: Uuid;
}

export interface CargoEntrada {
  tipo: TipoCargo;
  descripcion: string;
  bruto: Decimal;
  motivo: string;
}

export interface CrearNota {
  empresaId: Uuid;
  clienteEntidadId: Uuid;
  canal: Canal;
  ubicacionId: Uuid;
  documentoSolicitado?: DocumentoNota;
  condicionPago?: CondicionPago;
  plazoDias?: number;
  observaciones?: string;
  /** Sin enviar: el backend usa el vendedor de la relación comercial del cliente. Otro exige permiso. */
  vendedorId?: Uuid | null;
  /** Obligatorio si el vendedor no es el del cliente (además del permiso). */
  cambioVendedor?: CambioVendedor;
  /** Sin enviar: la lista del cliente. Otra (de las permitidas) exige permiso. */
  listaPrecioId?: Uuid;
  /** Una de las direcciones del cliente; la valida el backend (y de ella deriva la zona). */
  direccionEntregaId?: Uuid | null;
  entrega?: EntregaNota;
  /** Día comprometido de entrega; null o ausente = el de la nota. */
  fechaEntrega?: string | null;
  /** Venta directa (punto de venta, factura o boleta directa): no es un pedido. */
  ventaDirecta?: boolean;
  /** 0107: el cliente paga lo pendiente al recibir este pedido (hoja de cobranza en la ODD). */
  cobroContraEntrega?: boolean;
  lineas: LineaEntrada[];
  cargos?: CargoEntrada[];
}

export interface EditarNota {
  version: number;
  documentoSolicitado?: DocumentoNota;
  condicionPago?: CondicionPago;
  plazoDias?: number;
  observaciones?: string | null;
  ubicacionId?: Uuid;
  vendedorId?: Uuid | null;
  cambioVendedor?: CambioVendedor;
  listaPrecioId?: Uuid;
  direccionEntregaId?: Uuid | null;
  entrega?: EntregaNota;
  /** Día comprometido de entrega; null o ausente = el de la nota. */
  fechaEntrega?: string | null;
  cobroContraEntrega?: boolean;
  lineas?: LineaEntrada[];
  cargos?: CargoEntrada[];
}

export interface Totales {
  bruto: Decimal;
  neto: Decimal;
  impuestos: Array<{ codigo: string; monto: Decimal }>;
  totalImpuestos: Decimal;
}

export interface ResumenNota {
  notaId: Uuid;
  numero: string;
  numeroVisible: string;
  estado: EstadoNota;
  revision: number;
  version: number;
  totales?: Totales;
  lineasSinPrecio?: number[];
}

export interface ResultadoConfirmar extends ResumenNota {
  modoAprobacion: string | null;
  ambitos: string[];
  solicitudId: Uuid | null;
  credito: { resultado: ResultadoCredito; motivos: string[]; avisos: string[] } | null;
  reserva: {
    modo: string;
    requerido: Decimal;
    reservado: Decimal;
    pendiente: Decimal;
  } | null;
}

export interface LineaNota {
  numero: number;
  /** Lista con que se precio la línea (al previsualizar, solo si difiere de la de la nota). */
  listaPrecioId?: Uuid;
  productoId: Uuid;
  presentacionId: Uuid;
  claseLinea: string;
  sku: string;
  descripcion: string;
  presentacionCodigo: string;
  unidadCodigo: string;
  factorABase: Decimal;
  cantidad: Decimal;
  cantidadBase: Decimal;
  cantidadBaseProducto: Decimal;
  origenPrecio: OrigenPrecio | null;
  escalaDesde: Decimal | null;
  escalaHasta: Decimal | null;
  precioUnitarioBase: Decimal | null;
  precioLista: Decimal | null;
  precioReferencia: Decimal | null;
  precioAplicado: Decimal | null;
  motivoPrecio: string | null;
  descuentoTipo: 'PORCENTAJE' | 'MONTO' | null;
  descuentoValor: Decimal | null;
  descuento: Decimal;
  recargo: Decimal;
  bruto: Decimal | null;
  neto: Decimal | null;
}

export interface CargoNota {
  tipo: TipoCargo;
  descripcion: string;
  bruto: Decimal;
  neto: Decimal | null;
  motivo?: string;
}

export interface LineaReserva {
  numero: number;
  lineaId: Uuid;
  productoId: Uuid;
  confirmada: Decimal;
  despachada: Decimal;
  requerida: Decimal;
  reservada: Decimal;
  pendiente: Decimal;
}

export interface EstadoReservas {
  notaId: Uuid;
  estado: EstadoNota;
  ubicacionId: Uuid;
  requerido: Decimal;
  reservado: Decimal;
  pendiente: Decimal;
  lineas: LineaReserva[];
  decisiones: Array<{
    id: Uuid;
    causa: string;
    modo: string;
    requerido: Decimal;
    reservado: Decimal;
    pendiente: Decimal;
    decidido_por: Uuid;
    decidido_en: string;
  }>;
}

export interface DecisionCreditoFila {
  id: Uuid;
  revision: number;
  condicion_pago: string;
  plazo_dias: number;
  monto: Decimal;
  fuente: string;
  cupo: Decimal | null;
  resultado: ResultadoCredito;
  motivos: string[];
  evaluado_por: Uuid;
  evaluado_en: string;
}

export interface SolicitudAprobacion {
  id: Uuid;
  revision: number;
  ambitos: string[];
  exige_segregacion: boolean;
  solicitada_por: Uuid;
  estado: 'PENDIENTE' | 'APROBADA' | 'RECHAZADA' | 'RETIRADA' | 'CADUCADA' | string;
  resuelta_por: Uuid | null;
  resuelta_en: string | null;
  motivo_resolucion: string | null;
  creado_en: string;
}

export interface Transicion {
  id: Uuid;
  accion: string;
  estado_desde: string | null;
  estado_hasta: string;
  revision: number;
  actor_usuario_id: Uuid | null;
  actor: string;
  motivo: string | null;
  en: string;
}

export interface Nota {
  notaId: Uuid;
  numero: string;
  numeroVisible: string;
  estado: EstadoNota;
  revision: number;
  version: number;
  empresaId: Uuid;
  /** Ausente en respuestas antiguas: se trata como DESPACHO. */
  entrega?: EntregaNota;
  /** Día comprometido de entrega; null o ausente = el de la nota. */
  fechaEntrega?: string | null;
  /** Venta directa (punto de venta, factura o boleta directa): se muestra por su documento. */
  ventaDirecta?: boolean;
  /** 0115: se confirmó para emitir sin pago (con el permiso o con la firma SIN_PAGO). */
  sinPagoAutorizado?: boolean;
  /** Tiene un documento vigente (boleta, factura o vale). Sin él, la caja elige cuál emitir. */
  documentada?: boolean;
  /** 0107: el cliente paga lo pendiente al recibir este pedido. */
  cobroContraEntrega?: boolean;
  cabecera: {
    canal: Canal;
    clienteEntidadId: Uuid;
    clienteRut: string | null;
    clienteRazonSocial: string;
    clienteGiro: string | null;
    listaPrecioId: Uuid | null;
    ubicacionId: Uuid;
    documentoSolicitado: DocumentoNota;
    condicionPago: CondicionPago;
    plazoDias: number;
    fechaNegocio: string;
    observaciones: string | null;
    /** Vendedor comercial de la nota (no es quien digitó: ese es `digitadoPor`). */
    vendedorId?: Uuid | null;
    direccionEntregaId?: Uuid | null;
    direccionEntrega?: string | null;
    /** Zona logística sellada (derivada de la comuna de la dirección de despacho). */
    zonaLogisticaId?: Uuid;
    zonaLogisticaNombre?: string;
    /** Vencimiento del pago: lo calcula el backend (fecha de la nota + plazo). */
    fechaVencimiento?: string;
  };
  digitadoPor: Uuid | null;
  modoAprobacion: string | null;
  confirmadaEn: string | null;
  lineas: LineaNota[];
  cargos: CargoNota[];
  totales: Totales;
  cumplimiento: Array<{
    numero: number;
    estado: 'SIN_DESPACHO' | 'PARCIAL' | 'COMPLETA';
    despachadoBase: Decimal;
    pendienteBase: Decimal;
  }>;
  reservas: EstadoReservas;
  propuesta: unknown | null;
  credito: DecisionCreditoFila[];
  solicitudes: SolicitudAprobacion[];
  historia: Transicion[];
  /** Nombre de cada usuario que aparece en firmas e historia (lo agrega la API). */
  nombres?: Record<string, string>;
}

/**
 * 0122 (Rodrigo, 2026-10-27): autorización en línea para que el punto de venta emita sin pago una
 * nota confirmada. Vale una vez: al emitir queda USADA con su documento.
 */
export interface AutorizacionSinPago {
  id: Uuid;
  empresaId: Uuid;
  notaId: Uuid;
  revision: number;
  monto: Decimal;
  documento: DocumentoNota;
  estado: 'PENDIENTE' | 'APROBADA' | 'RECHAZADA' | 'RETIRADA' | 'USADA';
  solicitadaPor: Uuid;
  solicitadaPorNombre: string | null;
  solicitadaEn: string;
  resueltaPor: Uuid | null;
  resueltaPorNombre: string | null;
  resueltaEn: string | null;
  comentario: string | null;
  documentacionId: Uuid | null;
  usadaEn: string | null;
  version: number;
}

/** Firmas y tesorería: la autorización con su nota y lo que se emitió. */
export interface FilaSinPago extends AutorizacionSinPago {
  numeroVisible: string;
  cliente: string;
  clienteRut: string | null;
  condicionPago: string;
  emitido: {
    tipoDocumento: string | null;
    tipoDte: number | null;
    folio: string | null;
    valeNumero: string | null;
  } | null;
  puedeFirmar: boolean;
  impedimento: string | null;
}

export interface NotaListada {
  id: Uuid;
  empresa_id: Uuid;
  numero: string;
  numeroVisible: string;
  canal: Canal;
  estado: EstadoNota;
  cliente_entidad_id: Uuid;
  cliente_razon_social: string;
  cliente_rut?: string | null;
  documento_solicitado: DocumentoSolicitado;
  fecha_negocio: string;
  confirmada_en?: string | null;
  vendedor_id?: string | null;
  entrega?: EntregaNota;
  /** Día comprometido de entrega; null o ausente = el de la nota. */
  fechaEntrega?: string | null;
  /** Venta directa (punto de venta, factura o boleta directa), no un pedido. */
  venta_directa?: boolean;
  total?: string | null;
  /** Sus documentos tributarios vigentes (factura/boleta con folio), 2026-10-26. */
  documentos?: Array<{ documentoId: string; tipoDte: number; folio: string | null }>;
  revision: number;
  version: number;
}

// ---------------------------------------------------------------- previsualización (C-4, D-UI-1)

/** El cuerpo de previsualizar: el mismo de crear (el backend lo precia SIN guardar nada). */
export interface PrevisualizarNota extends CrearNota {
  fechaNegocio?: string;
  /** 0115: el documento saldrá sin pago asociado (documento directo). */
  sinPago?: boolean;
}

export interface DisponibilidadPedida {
  productoId: Uuid;
  ubicacionId: Uuid;
  requerido: Decimal;
  fisico: Decimal;
  reservado: Decimal;
  disponible: Decimal;
  /** Lo decide el backend (disponible ≥ requerido, en unidad base). */
  alcanza: boolean;
  stockPorRevisar?: string | null;
}

export interface Previsualizacion {
  cabecera: Nota['cabecera'];
  lineas: LineaNota[];
  cargos: CargoNota[];
  hashContenido: string;
  totales: Totales;
  lineasSinPrecio: number[];
  credito: {
    resultado: ResultadoCredito;
    motivos: string[];
    avisos: string[];
    fuente: string;
    cupo: Decimal | null;
    /** Con fuente de deuda (D-118): lo que debe el cliente, lo vencido y notas que ya consumen cupo. */
    exposicion?: Decimal | null;
    vencidos?: Decimal | null;
    compromisos?: Decimal;
    /** Línea − deuda − compromisos; null sin fuente o sin línea. */
    disponible?: Decimal | null;
  };
  aprobaciones: { modo: string; requeridos: string[] };
  validaciones: Array<{ codigo: string; mensaje: string; lineas?: number[] }>;
  /** null si el usuario no tiene permiso de ver inventario. */
  disponibilidad: DisponibilidadPedida[] | null;
}

// ---------------------------------------------------------------- despacho y DTE

export interface CumplimientoLinea {
  numero: number;
  lineaId: Uuid;
  productoId: Uuid;
  confirmado: Decimal;
  despachado: Decimal;
  devuelto: Decimal;
  pendienteDespacho: Decimal;
  reservado: Decimal;
  pendienteReserva: Decimal;
  estado: 'SIN_DESPACHO' | 'PARCIAL' | 'COMPLETA';
  reservasPorUbicacion: Uuid[];
}

export interface Cumplimiento {
  notaId: Uuid;
  estado: EstadoNota;
  revision: number;
  lineas: CumplimientoLinea[];
}

export interface Despacho {
  id: Uuid;
  numero: string;
  nota_id: Uuid;
  modalidad: ModalidadDespacho;
  ubicacion_id: Uuid;
  estado: EstadoDespacho;
  fecha_negocio: string;
  observaciones: string | null;
  confirmado_por: Uuid | null;
  confirmado_en: string | null;
  version: number;
  creado_por: Uuid;
  creado_en: string;
  lineas?: Array<{
    numero: number;
    nota_linea_id: Uuid;
    producto_id: Uuid;
    cantidad_base: Decimal;
  }>;
  historia?: Transicion[];
}

export interface DespachoCreado {
  despachoId: Uuid;
  numero: string;
  estado: EstadoDespacho;
  modalidad: ModalidadDespacho;
  ubicacionId: Uuid;
  version: number;
  lineas: Array<{ lineaNumero: number; cantidadBase: Decimal }>;
}

/** Estado de un DTE tal como lo proyecta el backend para el linaje de Ventas (agnóstico del motor). */
export interface EstadoDteVenta {
  documentoId: Uuid;
  numero: string;
  tipoDte: number;
  folio: number | null;
  estado: string;
  estadoEnvio: string;
  estadoSii: string;
  anulacion: string | null;
}

export interface DocumentacionNota {
  notaId: Uuid;
  documentos: Array<{
    documentacionId: Uuid;
    /** VIGENTE / ANULADA (la documentación de Ventas, no el DTE). */
    estado: string;
    tipoDocumento: string;
    dte: EstadoDteVenta | null;
    /** 0109: vale de venta (documento interno, sin DTE). */
    vale?: { id: Uuid; numero: string; motivo: string; total: Decimal } | null;
    lineas: number[];
    despachos: Uuid[];
  }>;
  lineas: Array<{
    numero: number;
    clase: string;
    pedido: Decimal;
    despachado: Decimal;
    documentado: Decimal;
  }>;
}

/**
 * Estado OPERACIONAL del DTE (C-6): lo deriva el backend con `estadoOperacional` de @madeci/dte (los
 * nombres de H4). Si el documento no está emitido, es el estado del documento. La interfaz nunca lo
 * deriva ni sabe qué motor emite (MadeciNative o Timbrado).
 */
export const ESTADOS_OPERACIONALES_DTE = [
  'SOLICITADO',
  'FALLIDO',
  'ANULADO_SIN_EMISION',
  'ANULADO_PRE_ENVIO',
  'EN_CONFLICTO',
  'PENDIENTE_ENVIO',
  'ENVIANDO',
  'REQUIERE_CONCILIACION',
  'CONFLICTO_DTE_SII',
  /** Estado SII de un conflicto (DNK); la base solo lo admite junto a CONFLICTO_SII. */
  'CONFLICTO',
  'ENVIO_RECHAZADO',
  'TRACKID_ASIGNADO',
  'PROCESANDO',
  'ACEPTADO',
  'ACEPTADO_CON_REPAROS',
  'RECHAZADO',
] as const;
export type EstadoOperacionalDte = (typeof ESTADOS_OPERACIONALES_DTE)[number];

export interface EstadoDte {
  documentoId: Uuid;
  folio: number | null;
  tipoDte: number;
  estados: { documento: string; generacion: string; firma: string; envio: string; sii: string };
  operacional: EstadoOperacionalDte | string;
  trackId: string | null;
}

export interface DocumentoDte extends EstadoDte {
  empresaId: Uuid;
  numero: string;
  historial: Array<{
    dimension: string;
    valor: string;
    codigo_externo: string | null;
    detalle: Record<string, unknown> | null;
    origen: string;
    ocurrido_en: string;
  }>;
}

export interface DocumentoDteListado {
  id: Uuid;
  numero: string;
  tipo_dte: number;
  folio: number | null;
  estado: string;
  estado_envio: string;
  estado_sii: string;
  monto_total: string | null;
  fecha_emision: string | null;
  origen_id: Uuid;
  creado_en: string;
}

export interface Categoria {
  id: Uuid;
  codigo: string;
  nombre: string;
  padre_id?: Uuid | null;
  nivel?: number;
}
export interface Marca {
  id: Uuid;
  codigo: string;
  nombre: string;
}

// ---------------------------------------------------------------- errores

/** El sobre único de error de la API (apps/api/src/plugins/errores.ts). */
export interface CuerpoError {
  error: string;
  mensaje: string;
  detalle:
    ({ codigo?: string; permiso?: string; motivo?: string } & Record<string, unknown>) | null;
  correlacion_id?: string;
}

// ---------------------------------------------------------------- caja · mesón (fase 1)
export type MedioCaja = 'EFECTIVO' | 'DEBITO' | 'CREDITO' | 'TRANSFERENCIA' | 'CHEQUE';
export interface MedioRecibo {
  medio: MedioCaja;
  monto: string;
  terminal?: string | null;
  autorizacion?: string | null;
  cuotas?: number | null;
  banco?: string | null;
  numeroDocumento?: string | null;
  fechaDocumento?: string | null;
  referencia?: string | null;
}
export interface AplicacionRecibo {
  id: string;
  tipo: 'APLICACION' | 'REVERSA';
  documentoId: string;
  tipoDte: number | null;
  folio: string | null;
  monto: string;
  origen: 'AUTOMATICA' | 'MANUAL';
  eventoId: string | null;
  aplicadoPor: string;
  aplicadoEn: string;
}
export interface ReciboCaja {
  id: string;
  numeroVisible: string;
  total: string;
  estado: 'VIGENTE' | 'ANULADO';
  version: number;
  observacion: string | null;
  creadoEn: string;
  creadoPor: string;
  motivoAnulacion: string | null;
  medios: MedioRecibo[];
  aplicaciones: AplicacionRecibo[];
}
export type EstadoPagoNota = 'SIN_PAGO' | 'PARCIAL' | 'PAGADA';
/** El estado de pago de una nota: lo ve todo el proceso (nota, despacho, documento). */
export interface PagoNota {
  notaId: string;
  nota: string;
  total: string;
  pagado: string;
  saldo: string;
  aplicado: string;
  estado: EstadoPagoNota;
  recibos: ReciboCaja[];
}
export interface ReciboRegistrado {
  reciboId: string;
  numeroVisible: string;
  total: string;
  estadoPago: EstadoPagoNota;
  /** Fase 2a: el turno de caja donde quedó el cobro (null: transferencia sin turno). */
  turno?: string | null;
  medios?: { medio: MedioCaja; monto: string }[];
}

// ---------------------------------------------------------------- mesón · fase 2a: turno de caja

export interface PuntoCobro {
  id: string;
  codigo: string;
  nombre: string;
  activo: boolean;
  /** Nombre del cajero con turno abierto en el punto (null: libre). */
  ocupadoPor: string | null;
  /** 0130: caja de mesón sin dueño (admite elegibles, operadores y política de cierre). */
  compartible?: boolean;
  /** Caja personal: su dueño, el único que abre turno en ella (null: mesón o tesorería). */
  usuarioId?: string | null;
  /** MESON, PERSONAL o TESORERIA. */
  tipo?: 'MESON' | 'PERSONAL' | 'TESORERIA';
  /** 0133: el puesto donde está la caja de un mesón (null: sin puesto). */
  puesto?: number | null;
  /** 0133: la persona a la que está asociada la caja de tesorería. */
  titularId?: string | null;
  titular?: string | null;
  /** 0132: el turno abierto de la caja (null: está libre). */
  turnoId?: string | null;
  /** 0132: el turno abierto se puede compartir (un mesón con responsable personal, no legado). */
  turnoCompartible?: boolean;
  /** 0132: quienes cobran en el turno abierto además de su responsable. */
  compartenCon?: { usuarioId: string; nombre: string }[];
}

export type EstadoTurno = 'ABIERTO' | 'CERRADO' | 'REVISADO';

export interface FilaArqueo {
  medio: MedioCaja;
  esperado: string;
  contado: string | null;
  diferencia: string | null;
  cantidad: number | null;
}

export interface TurnoCaja {
  id: string;
  numeroVisible: string;
  empresaId: string;
  punto: { codigo: string; nombre: string };
  estado: EstadoTurno;
  version: number;
  cajeroId: string;
  cajero: string;
  abiertoEn: string;
  cerradoEn: string | null;
  fondoInicial: string;
  observacionCierre: string | null;
  revision: {
    resultado: 'CONFORME' | 'CON_REPARO';
    por: string;
    en: string;
    nota: string | null;
    recontadoEfectivo: string | null;
  } | null;
  recibos: {
    id: string;
    numeroVisible: string;
    notaId: string | null;
    nota: string | null;
    total: string;
    estado: 'VIGENTE' | 'ANULADO';
    creadoEn: string;
    medios: { medio: MedioCaja; monto: string }[];
    /** 0130: quién cobró (en un turno compartido, la responsable o un operador). */
    cobradoPor?: { id: string; nombre: string };
    /** V4: la solicitud de anulación pendiente (la ejecuta tesorería). */
    anulacionPendiente?: { solicitudId: string; causa: CodigoCausa } | null;
  }[];
  movimientos: {
    id: string;
    tipo: 'RETIRO' | 'INGRESO';
    monto: string;
    motivo: string;
    creadoEn: string;
  }[];
  /** Solo con caja.turno.ver y el turno abierto (el cajero cierra a ciegas). */
  esperadoEnVivo: { medio: MedioCaja; esperado: string }[] | null;
  arqueo: FilaArqueo[] | null;
  aRendirEfectivo: string | null;
  /** 0130 (solo «mi turno»): RESPONSABLE abre, cierra y arquea; OPERADOR solo cobra. */
  miRol?: 'RESPONSABLE' | 'OPERADOR';
  responsable?: { id: string; nombre: string };
  /** 0130: operadores habilitados en el turno (vacío: no es compartido). */
  operadores?: OperadorTurno[];
  /** 0130: con política de cierre, NORMAL (sin revisión) o EXCEPCION; null: flujo de siempre. */
  cierreClase?: ClaseCierre | null;
  excepcion?: ResumenExcepcion | null;
  /** V5: el modo del turno (null: anterior a la 0130). */
  calidadIdentidad?: CalidadIdentidad | null;
  personaResponsable?: 'IDENTIFIED' | 'UNKNOWN' | null;
  /** V4: abierto por una cuenta compartida de puesto: persona desconocida. */
  legado?: { persona: 'UNKNOWN' } | null;
}

// ---------------------------------------------------------------- 0130: turno compartido

/**
 * V5 · el modo del turno: PERSONAL_ATTRIBUTED (lo abrió una identidad personal lista) o LEGACY_SHARED
 * (lo abrió una cuenta compartida de puesto vigente: la persona responsable es desconocida).
 */
export type CalidadIdentidad = 'PERSONAL_ATTRIBUTED' | 'LEGACY_SHARED';
export type ClaseCierre = 'NORMAL' | 'EXCEPCION';
export type MotivoExcepcion =
  | 'DIFERENCIA_EFECTIVO'
  | 'MATERIAL'
  | 'DIFERENCIA_TARJETA'
  | 'DIFERENCIA_CHEQUE'
  | 'COBRO_ANULADO'
  | 'POST_CLOSE_VOID_EXCEPTION'
  | 'ANULACION_MATERIAL'
  | 'ANULACION_SIN_SOLICITUD'
  | 'ANULACION_NO_VINCULABLE'
  | 'ANULACION_CON_DESCUADRE'
  | 'ANULACION_MEDIO_NO_CUADRA'
  | 'ANULACION_VIOLA_POLITICA'
  | 'ANULACION_SENAL'
  | 'CORRECCION_POSTERIOR'
  | 'CORRECCION_MATERIAL'
  | 'CORRECCION_CAMBIA_EFECTIVO'
  | 'CORRECCION_TARJETA_CHEQUE'
  | 'CORRECCION_SIN_EVIDENCIA'
  | 'CORRECCION_VIOLA_POLITICA'
  | 'CORRECCION_CON_DESCUADRE'
  | 'INCIDENTE_SENSIBLE'
  | 'REINCIDENCIA_OPERADOR'
  | 'REINCIDENCIA_CAJA'
  | 'SIN_REVISOR_NIVEL_UNO';
/**
 * Nivel 1: PRINCIPAL (soporte comercial) o ALTERNO (tesorería o administración de cajas). Nivel 2:
 * PRINCIPAL (aprobación superior), ALTERNO (alterna de nivel 2) u OPEN_REQUIRES_INDEPENDENT_REVIEW (V4:
 * nadie sin conflicto; abierta, avisada y sin autoaprobación).
 */
export type ColaRevision = 'PRINCIPAL' | 'ALTERNO' | 'OPEN_REQUIRES_INDEPENDENT_REVIEW';
/** V4 · señales de anomalía de una anulación (no son fraude) y la informativa del turno. */
export type CodigoSenal =
  | 'MULTIPLE_VOIDS_SAME_OPERATOR'
  | 'MULTIPLE_VOIDS_SAME_SHIFT'
  | 'VOID_AFTER_CLOSE'
  | 'HIGH_VALUE_VOID'
  | 'VOID_WITHOUT_REQUEST'
  | 'VOID_WITHOUT_LINKED_DOCUMENT'
  | 'VOID_PAYMENT_METHOD_MISMATCH'
  | 'VOID_AFTER_SETTLEMENT'
  | 'REPEATED_VOID_CUSTOMER'
  | 'EXCEPTIONAL_OPERATOR';
export interface Senal {
  codigo: CodigoSenal;
  reciboId: string | null;
  escala: boolean;
  detalle?: Record<string, unknown>;
}
export type MotivoEscalamiento =
  'ANULACION_SOSPECHOSA' | 'DISCREPANCIA_NO_RESUELTA' | 'EXCEPCION_POLITICA' | 'OTRO';
export type ResolucionExcepcion =
  'JUSTIFICADA' | 'CARGO_RESPONSABLE' | 'ERROR_DE_REGISTRO' | 'OTRA';
/** V3 · el código de causa de un clic al resolver (texto solo para OTHER o lo que pida la política). */
export type CodigoCausa =
  | 'COUNTING_ERROR'
  | 'CHANGE_ERROR'
  | 'VOIDED_PAYMENT'
  | 'PAYMENT_METHOD_MISMATCH'
  | 'ROUTE_CASH'
  | 'LATE_ENTRY'
  | 'SYSTEM_ERROR'
  | 'AUTHORIZED_CORRECTION'
  | 'OTHER';
/**
 * V3 · RESPONSABLE: puede abrir la caja y ser la responsable de un turno; OPERADOR: puede ser habilitada;
 * V4 · OPERADOR_EXCEPCIONAL: se habilita, pero queda marcada como participación excepcional.
 */
export type FuncionElegible = 'RESPONSABLE' | 'OPERADOR' | 'OPERADOR_EXCEPCIONAL';

/** V4 · una solicitud de anulación (la pide el turno, la ejecuta o rechaza tesorería). */
export interface SolicitudAnulacion {
  solicitudId: string;
  reciboId: string;
  recibo: string;
  reciboVersion: number;
  nota: string | null;
  total: string;
  turnoId: string;
  turno: string;
  turnoCerrado: boolean;
  caja: string;
  causa: CodigoCausa;
  notaSolicitud: string | null;
  solicitadoPor: { id: string; nombre: string };
  solicitadoEn: string;
  estado: 'PENDIENTE' | 'EJECUTADA' | 'RECHAZADA';
  resueltaPor: { id: string; nombre: string } | null;
  resueltaEn: string | null;
  notaResolucion: string | null;
}

/** V4 · una cuenta compartida de puesto (legado). */
export interface CuentaCompartida {
  cuentaCompartidaId: string;
  usuarioId: string;
  cuenta: string;
  etiqueta: string;
  vigenteDesde: string;
  vigenteHasta: string | null;
  vigente: boolean;
  otorgadoPor: string;
  retiradoEn: string | null;
}

export interface ResumenExcepcion {
  excepcionId: string;
  nivel: number;
  estado: 'ABIERTA' | 'RESUELTA';
  motivos?: MotivoExcepcion[];
}

export interface OperadorTurno {
  usuarioId: string;
  nombre: string;
  autorizadoPor: string;
  autorizadoPorNombre: string;
  autorizadoEn: string;
  retiradoPor: string | null;
  retiradoEn: string | null;
  causaRetiro: 'RETIRADO' | 'CIERRE_TURNO' | 'ELEGIBILIDAD_RETIRADA' | null;
  /** V4: habilitada como operadora excepcional. */
  excepcional?: boolean;
  /** 0132: se sumó ella misma con «Compartir caja». */
  compartio?: boolean;
}

/** Un elegible de la caja visto desde el turno: si ya opera, si está libre o si tiene otra caja. */
export interface ElegibleTurno {
  usuarioId: string;
  nombre: string;
  estado: 'HABILITADO' | 'DISPONIBLE' | 'CON_TURNO_PROPIO' | 'EN_OTRA_CAJA';
  otraCaja: string | null;
  /** V4: solo elegible como operadora excepcional (queda marcada al habilitarla). */
  excepcional?: boolean;
}

export interface ElegibleCaja {
  elegibilidadId: string;
  usuarioId: string;
  /** V3: la función en esa caja (sin dato: OPERADOR). */
  funcion?: FuncionElegible;
  nombre: string;
  otorgadoPor: string;
  otorgadoEn: string;
  observacion: string | null;
  retiradoPor: string | null;
  retiradoEn: string | null;
}

export interface ElegiblesPunto {
  punto: { id: string; codigo: string; nombre: string; compartible: boolean };
  /** V2: operadores del turno anterior que siguen elegibles y activos (se preseleccionan al abrir). */
  sugeridos?: string[];
  /** Operadoras elegibles vigentes. */
  vigentes: ElegibleCaja[];
  /** V3: responsables elegibles vigentes (vacío: cualquiera con caja.turno.operar abre, como hoy). */
  responsables?: ElegibleCaja[];
  historial: ElegibleCaja[];
}

export interface CandidatoCaja {
  id: string;
  nombre: string;
  correo: string;
}

export interface PoliticaCierre {
  activa: boolean;
  umbralMaterial: string;
  reincidenciaOperadorCantidad: number;
  reincidenciaOperadorDias: number;
  reincidenciaCajaCantidad: number;
  reincidenciaCajaDias: number;
  /** V4: NIVEL_1 = la anulación normal crea una excepción de nivel 1; REVISION_NORMAL = no la crea sola. */
  anulacionNormal: 'NIVEL_1' | 'REVISION_NORMAL';
  /** V3: desde este monto (inclusive) un cobro anulado (o corregido después) es material (nivel 2). */
  umbralAnulacionMaterial: string;
  /** V4: horas después del cierre en que anular o corregir es período permitido (null: mismo turno). */

  senalAnulacionesOperadorCantidad: number;
  senalAnulacionesOperadorDias: number;
  senalAnulacionesTurnoCantidad: number;
  senalAnulacionesClienteCantidad: number;
  senalAnulacionesClienteDias: number;
  senalesQueEscalan: CodigoSenal[];
  causasCorreccionPermitidas: CodigoCausa[];
  /** V3: códigos de causa que piden texto al resolver (OTHER, siempre). */
  codigosConTexto: CodigoCausa[];
  observacion: string | null;
  politicaId?: string;
  creadoPor?: string;
  creadoEn?: string;
}

export interface ExcepcionCierre {
  excepcionId: string;
  turnoId: string;
  turno: string;
  caja: string;
  /** CIERRE, POST_CLOSE_VOID_EXCEPTION (V5: siempre nivel 2) o (V4) CORRECCION_POSTERIOR. */
  origen?: 'CIERRE' | 'POST_CLOSE_VOID_EXCEPTION' | 'CORRECCION_POSTERIOR';
  /** V5: el modo del turno de la excepción. */
  calidadIdentidad?: CalidadIdentidad | null;
  reciboId?: string | null;
  responsable: { id: string; nombre: string };
  cerradoEn: string | null;
  diferenciaEfectivo: string;
  diferencias: Record<string, string | null>;
  anulaciones: number;
  brechasRuta?: number;
  motivos: MotivoExcepcion[];
  nivelInicial: number;
  nivel: number;
  cola?: ColaRevision | null;
  /** V4: nivel 2 sin nadie libre de conflicto: abierta, visible, avisada y sin autoaprobación. */
  requiereRevisionIndependiente?: boolean;
  senalesCantidad?: number;
  estado: 'ABIERTA' | 'RESUELTA';
  creadaEn: string;
  resolucion: {
    causa?: CodigoCausa | null;
    autorizacion?: { por: string; referencia: string | null };
    resolucion: ResolucionExcepcion;
    nota: string | null;
    por: string;
    en: string;
  } | null;
}

export interface ExcepcionCierreDetalle extends ExcepcionCierre {
  fondoInicial: string;
  observacionCierre: string | null;
  operadores: { id: string; nombre: string }[];
  /** Quienes participaron en el turno: no la resuelven (conflicto de interés). */
  participantes?: { id: string; nombre: string }[];
  reincidencias?: Record<string, unknown>;
  /** V3/V4: cada cobro anulado (o corrección posterior) con su nivel, causas y señales. */
  anulacionesDetalle?: AnulacionExcepcion[];
  /** V4: señales de anomalía (no son fraude). */
  senales?: Senal[];
  /** V4: quienes ejecutaron la anulación o corrección que la causó: no la revisan. */
  actores?: { id: string; nombre: string }[];
  /** V4: quién puede figurar como «aprobó o pidió» una corrección autorizada. */
  autorizadores?: { id: string; nombre: string }[];
  /** V4: el turno lo abrió una cuenta compartida de puesto. */
  legado?: boolean;
  arqueo: FilaArqueo[];
  politica: PoliticaCierre;
  escalamiento: {
    por: string;
    en: string;
    motivo?: MotivoEscalamiento | null;
    nota: string | null;
  } | null;
  /** Lo que ESTA persona puede hacer (con conflicto de interés no ve «resolver»). */
  acciones?: { resolver: boolean; escalar: boolean; razon: string | null };
}

/** V3/V4 · un cobro anulado (o una corrección posterior) medido con las reglas de nivel 1 / 2. */
export interface AnulacionExcepcion {
  tipo?: 'ANULACION' | 'CORRECCION';
  reciboId: string;
  numero: string;
  monto: string;
  nivel?: 1 | 2;
  causas?: string[];
  senales?: CodigoSenal[];
  anuladoPor?: string | null;
  anuladoPorNombre: string | null;
  anuladoEn?: string | null;
  solicitudId?: string | null;
  solicitadoPorNombre?: string | null;
  causaSolicitud?: CodigoCausa | null;
  corregidoPorNombre?: string | null;
  causa?: CodigoCausa | null;
}

export interface OperacionAuditoriaTurno {
  tipo: string;
  en: string;
  operador: { id: string; nombre: string } | null;
  sesion: {
    id: string;
    ip: string | null;
    dispositivo: string | null;
    origen: string | null;
  } | null;
  referencia: string;
  detalle: Record<string, unknown>;
}

export interface AuditoriaTurno {
  turno: {
    id: string;
    numeroVisible: string;
    estado: EstadoTurno;
    caja: { codigo: string; nombre: string; tipo: string };
    compartido: boolean;
    responsable: { id: string; nombre: string };
    cierreClase: ClaseCierre | null;
    excepcion: ResumenExcepcion | null;
    /** V3: cuántas excepciones tiene (la de cierre y las posteriores). */
    excepciones?: number;
    /** V5: el modo del turno, explícito. */
    calidadIdentidad?: CalidadIdentidad | null;
    personaResponsable?: 'IDENTIFIED' | 'UNKNOWN' | null;
    /** V4: abierto por una cuenta compartida de puesto: persona desconocida. */
    legado?: { cuenta: string; persona: 'UNKNOWN' } | null;
  };
  operaciones: OperacionAuditoriaTurno[];
  porPersona: {
    persona: { id: string; nombre: string };
    cobros: number;
    cobrado: string;
    efectivo: string;
    anulaciones: number;
  }[];
}

export interface TurnoListado {
  id: string;
  numeroVisible: string;
  punto: string;
  estado: EstadoTurno;
  revision: 'CONFORME' | 'CON_REPARO' | null;
  cajero: string;
  abiertoEn: string;
  cerradoEn: string | null;
  fondoInicial: string;
  diferencia: string | null;
  descuadres: number | null;
  /** 0130: con política de cierre; null o ausente: flujo de revisión de siempre. */
  cierreClase?: ClaseCierre | null;
  excepcion?: ResumenExcepcion | null;
  /** V5: el modo del turno (null: anterior a la 0130). */
  calidadIdentidad?: CalidadIdentidad | null;
  /** V4: abierto por una cuenta compartida de puesto. */
  legado?: boolean;
}

export interface CierreTurno {
  turnoId: string;
  estado: 'CERRADO';
  numero: string;
  arqueo: FilaArqueo[];
  aRendirEfectivo: string;
  /** docs/111 Etapa 2 · lo que esta caja entrega a tesorería (null: es tesorería o no rinde nada). */
  entrega?: {
    entregaId: string;
    efectivo: string;
    cheques: string;
    chequesCantidad: number;
  } | null;
  cuadra: boolean;
  /** 0130: solo si la caja tiene política de cierre. */
  /** V4: abierto por una cuenta compartida de puesto. */
  legado?: { persona: 'UNKNOWN' };
  cierre?: {
    clase: ClaseCierre;
    /** V4: señales (no son fraude), también si el cierre fue normal. */
    senales?: CodigoSenal[];
    /** 0: normal; 1 o 2: excepción. */
    nivel?: number;
    excepcion: {
      excepcionId: string;
      nivel: number;
      cola?: ColaRevision | null;
      motivos: MotivoExcepcion[];
    } | null;
  };
}
